import { gql } from '@apollo/client'

import {
  type LoadOptionsForPickListParams,
  isDistinctValuesFilterInputType,
} from '../components/FilterEditor/FilterEditor/FilterEditor.parts'
import { apolloClient } from './apolloClient'

const capitalize = (s: string): string =>
  s.length === 0 ? s : s[0].toUpperCase() + s.slice(1)

/**
 * Builds the query name for distinct values: Distinct[ModelName][FieldName]Query.
 * e.g. PlaceFilterInput + alignedLabel -> DistinctPlaceAlignedLabelQuery
 */
const getDistinctQueryName = (
  filterInputTypeName: string,
  fieldName: string,
): string => {
  const modelName = filterInputTypeName.replace(/FilterInput$/i, '') || 'Filter'
  const fieldPart = fieldName.split(/[._-]/).map(capitalize).join('')
  return `Distinct${modelName}${fieldPart}Query`
}

/**
 * Key under distinctValues is the filter input type name (e.g. PlaceFilterInput).
 * Strip any query prefix the CMS may pass so we get the short type name.
 */
const getDistinctValuesKey = (filterInputTypeName: string): string => {
  const afterQuery = filterInputTypeName.replace(/^.*?Query/, '')
  return afterQuery || filterInputTypeName
}

/**
 * Returns a GraphQL document for fetching distinct values.
 * Query shape: electrumpython { distinctValues { [FilterInputType] { [fieldName](first, search, notIn) } } }
 * Field name must exist on that type's DistinctFields (use unmapped field key from field config meta).
 */
export const getDistinctValuesQueryDocument = (
  filterInputTypeName: string,
  fieldName: string,
) => {
  const key = getDistinctValuesKey(filterInputTypeName)
  const queryName = getDistinctQueryName(filterInputTypeName, fieldName)
  const queryString = [
    `query ${queryName}($first: Int, $search: String, $notIn: [String], $skip: Int, $rootFilter: JSON, $unmappedPath: JSON) {`,
    '  electrumpython {',
    '    distinctValues {',
    `      ${key} {`,
    `        ${fieldName}(first: $first, search: $search, notIn: $notIn, skip: $skip, rootFilter: $rootFilter, unmappedPath: $unmappedPath)`,
    '      }',
    '    }',
    '  }',
    '}',
  ].join('\n')
  return gql(queryString)
}

export type DistinctValuesQueryVariables = {
  first?: number | null
  search?: string | null
  notIn?: Array<string | null> | null
  skip?: number | null
  rootFilter?: Record<string, unknown> | null
  unmappedPath?: Array<[string, string]> | null
}

/**
 * Fetches distinct values via the electrumpython distinctValues API.
 * fieldName must be a field that exists on that type's DistinctFields (use unmapped field key from field config meta).
 * Returns options as { label, value } (value is the string, label same as value).
 */
export const fetchDistinctValues = async (
  filterInputTypeName: string,
  fieldName: string,
  variables: DistinctValuesQueryVariables,
): Promise<Array<{ label: string; value: string }>> => {
  const key = getDistinctValuesKey(filterInputTypeName)
  const document = getDistinctValuesQueryDocument(
    filterInputTypeName,
    fieldName,
  )
  const result = await apolloClient.query({
    query: document,
    variables: {
      first: variables.first ?? 250,
      search: variables.search ?? null,
      notIn: variables.notIn ?? null,
      skip: variables.skip ?? null,
      rootFilter: variables.rootFilter ?? null,
      unmappedPath: variables.unmappedPath ?? null,
    },
  })
  const data = result.data as {
    electrumpython?: {
      distinctValues?: {
        [key: string]: { [key: string]: string[] | null } | null
      } | null
    } | null
  }
  const arr = data?.electrumpython?.distinctValues?.[key]?.[fieldName]
  if (!Array.isArray(arr)) return []
  return arr
    .map(item => ({ label: item, value: item }))
    .filter(opt => opt.label !== '' && opt.value !== '')
}

/**
 * Creates a loadOptions function for PickListFilter that uses the distinct values GraphQL query.
 * Nested fields use the correct FilterInput type via unmappedTypeName
 * (e.g. ShipmentDocumentFilterInput for carrier, not TransportUnitShipmentDocumentFilterInput).
 */
export const createDistinctValuesLoadOptions = () => {
  return ({
    unmappedTypeName,
    unmappedFieldKey,
    search,
    limit,
    notIn,
    skip,
    unmappedPath,
    rootFilter,
  }: LoadOptionsForPickListParams): Promise<
    Array<{ label: string; value: string }>
  > => {
    // Cube *WhereInput types are not registered on distinctValues — skip
    // rather than issuing an invalid GraphQL query (yellow pick-list error).
    if (!isDistinctValuesFilterInputType(unmappedTypeName)) {
      return Promise.resolve([])
    }
    if (!unmappedFieldKey) {
      return Promise.resolve([])
    }
    return fetchDistinctValues(unmappedTypeName ?? '', unmappedFieldKey, {
      first: limit ?? 250,
      search: search || null,
      notIn: notIn?.length ? notIn : null,
      skip: skip ?? null,
      // rootFilter: rootFilter ?? null,
      unmappedPath: unmappedPath ?? null,
    })
  }
}
